import { FORWARD, REVERSE } from "configs/lottie";
import { IntersectionObserverOptions } from "hooks/useIntersectionObserver";
import useMediaQuery from "hooks/useMediaQuery";
import useOnScreen from "hooks/useOnScreen";
import { Heading } from "iparque-components";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import React, { useCallback, useEffect, useRef } from "react";
import { breakpoints } from "theme/breakpoints";
import { AppearFromBottomProps } from "theme/staggeredAnimations";
import { ENDING_FRAME, SPEED, STARTING_FRAME } from "./configs";
import { Body, Container, lottieStyle } from "./styles";

export interface AdvantageInterface {
  animationData: Record<string, unknown>;
  title: string;
  body: string;
  ariaLabel: string;
}

interface Props extends AdvantageInterface, AppearFromBottomProps {}

const intersectionOptions: IntersectionObserverOptions = {
  threshold: 1,
};

const Advantage: React.FC<Props> = ({
  animationData,
  title,
  body,
  ariaLabel,
  isSectionVisible,
  index,
}) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(containerRef, intersectionOptions);
  const isMobileScreen = useMediaQuery({ maxWidth: breakpoints.md });

  useEffect(() => {
    lottieRef.current?.setSpeed(SPEED);
  }, []);

  const playForwardAnimation = useCallback(() => {
    lottieRef.current?.setDirection(FORWARD);
    lottieRef.current?.goToAndPlay(STARTING_FRAME, true);
  }, []);

  const playReverseAnimation = useCallback(() => {
    lottieRef.current?.setDirection(REVERSE);
    lottieRef.current?.goToAndPlay(ENDING_FRAME, true);
  }, []);

  useEffect(() => {
    if (!isMobileScreen) {
      return;
    }

    if (isOnScreen) {
      playForwardAnimation();
    } else {
      playReverseAnimation();
    }
  }, [isMobileScreen, isOnScreen, playForwardAnimation, playReverseAnimation]);

  return (
    <Container
      ref={containerRef}
      onMouseEnter={playForwardAnimation}
      onMouseLeave={playReverseAnimation}
      isSectionVisible={isSectionVisible}
      index={index}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        autoplay={false}
        loop={false}
        style={lottieStyle}
        role="img"
        aria-labelledby={ariaLabel}
        title={ariaLabel}
      />
      <Heading
        color="secondary"
        variant="h9"
        elementType="h3"
        className="uppercase mt-20"
      >
        {title}
      </Heading>
      <Body color="secondary" variant="body4" className="mt-10">
        {body}
      </Body>
    </Container>
  );
};

export default Advantage;
